import { useState } from 'react';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { FormTextarea } from '@/components/FormTextarea';
import { SlideOver } from '@/components/shared';
import { useToast } from '@/hooks/useToast';
import type { DataUpdateRequest, DayOffRequest, DpRequestMessage, TicketRequest } from '../../../../../shared/dpRequests';
import { ChangeReview } from '../ChangeReview';
import { AttachmentList } from '../AttachmentList';
import { StatusPill } from '../StatusPill';
import { TicketThread } from '../TicketThread';
import { withUploadedAttachments, type PendingFile } from '../../services/dpAttachmentUpload';
import { ClientApprovalSummary } from '../RequestSummaries';
import { HealthPlanReview, OvertimeReview, RaroInvestReview } from '../DpReviews';
import { REQUEST_TYPE_LABELS, describeRequestStatus } from '../../dpRequests.definitions';
import { formatDayOffDates } from '../../utils/formatDayOffDates';
import { useDecideDataUpdate, useMarkDayOffRegistered, usePostTicketMessage, useResolveTicket } from '../../hooks/data/useDpRequestMutations';
import { useDpMessagesQuery, useDpRequestQuery } from '../../hooks/data/useDpRequestQueries';
import type { DpRequestDrawerProps } from './dpRequestDrawer.types';

export function DpRequestDrawer({ requestId, dpEmail, onClose }: DpRequestDrawerProps) {
    const requestQuery = useDpRequestQuery(requestId);
    const request = requestQuery.data;
    const messagesQuery = useDpMessagesQuery(request?.type === 'ticket' ? requestId : null);

    return (
        <SlideOver
            isOpen={Boolean(requestId)}
            onClose={onClose}
            title={request ? request.collaboratorName || request.collaboratorEmail : 'Pedido'}
            subtitle={request ? REQUEST_TYPE_LABELS[request.type] : undefined}
        >
            {requestQuery.isPending && <div className="h-40 animate-pulse rounded-xl bg-raro-gray-100" />}
            {requestQuery.isError && <p className="text-sm text-rose-700">Não foi possível carregar este pedido.</p>}
            {request?.type === 'data_update' && <DataUpdateReview request={request} />}
            {request?.type === 'ticket' && <TicketReview request={request} dpEmail={dpEmail} messages={messagesQuery} />}
            {request?.type === 'day_off' && <DayOffReview request={request} />}
            {request?.type === 'overtime' && <OvertimeReview request={request} />}
            {request?.type === 'raro_invest' && <RaroInvestReview request={request} />}
            {request?.type === 'health_plan' && <HealthPlanReview request={request} />}
        </SlideOver>
    );
}

function DataUpdateReview({ request }: { request: DataUpdateRequest }) {
    const decide = useDecideDataUpdate();
    const toast = useToast();
    const [mode, setMode] = useState<'idle' | 'correct' | 'reject'>('idle');
    const [note, setNote] = useState('');
    const meta = describeRequestStatus(request);
    const reviewable = request.status === 'pending_dp';

    async function run(action: 'approve' | 'correct' | 'reject') {
        if (action !== 'approve' && note.trim() === '') {
            toast.error('Escreva o motivo', 'Ele aparece para o colaborador.');
            return;
        }
        try {
            await decide.mutateAsync({ requestId: request.id, action, note: note.trim() || undefined });
            setMode('idle');
            setNote('');
        } catch {
            return;
        }
    }

    return (
        <div className="space-y-6">
            <StatusPill label={meta.label} tone={meta.tone} />

            {request.decision?.note && <Note title="Última nota" text={request.decision.note} />}

            <section className="space-y-3">
                <h4 className="text-sm font-semibold text-raro-gray-800">Alterações pedidas</h4>
                <ChangeReview changes={request.changes} />
                <AttachmentList attachments={request.attachments} />
            </section>

            {reviewable && mode === 'idle' && (
                <div className="space-y-3 border-t border-raro-gray-200 pt-5">
                    <p className="text-xs text-raro-gray-500">Ao aprovar, os valores entram no cadastro. Se o cadastro tiver mudado desde o envio, o sistema não aplica e pede correção.</p>
                    <div className="grid gap-2 sm:grid-cols-3">
                        <Button variant="outline" onClick={() => setMode('reject')} disabled={decide.isPending}>Recusar</Button>
                        <Button variant="secondary" onClick={() => setMode('correct')} disabled={decide.isPending}>Pedir correção</Button>
                        <Button onClick={() => void run('approve')} loading={decide.isPending}>Aprovar</Button>
                    </div>
                </div>
            )}

            {reviewable && mode !== 'idle' && (
                <div className="space-y-3 border-t border-raro-gray-200 pt-5">
                    <FormTextarea
                        label={mode === 'reject' ? 'Motivo da recusa' : 'O que precisa ser corrigido'}
                        name="decision-note"
                        value={note}
                        onChange={event => setNote(event.target.value)}
                        maxLength={500}
                        showCharCount
                        rows={3}
                        required
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => { setMode('idle'); setNote(''); }} disabled={decide.isPending}>Voltar</Button>
                        <Button
                            variant={mode === 'reject' ? 'danger' : 'primary'}
                            onClick={() => void run(mode)}
                            loading={decide.isPending}
                        >
                            {mode === 'reject' ? 'Confirmar recusa' : 'Enviar pedido de correção'}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

function TicketReview({
    request,
    dpEmail,
    messages,
}: {
    request: TicketRequest;
    dpEmail: string;
    messages: { data?: DpRequestMessage[]; isPending: boolean };
}) {
    const { user } = useAuth();
    const post = usePostTicketMessage();
    const resolve = useResolveTicket();
    const meta = describeRequestStatus(request);
    const open = request.status === 'open' || request.status === 'waiting_collaborator';

    async function send(body: string, files: PendingFile[]) {
        if (!user?.uid) return;
        await withUploadedAttachments(files, user.uid, attachments =>
            post.mutateAsync({ requestId: request.id, body, attachments })
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
                <StatusPill label={meta.label} tone={meta.tone} />
                <span className="text-xs text-raro-gray-500">{request.category === 'reserved' ? 'Reservado' : 'Outro assunto'} · {request.subject}</span>
            </div>

            <TicketThread
                messages={messages.data ?? []}
                viewerEmail={dpEmail}
                isLoading={messages.isPending}
                canReply={open}
                replyPlaceholder="Responda o colaborador"
                isSending={post.isPending}
                onSend={send}
            />

            {open && (
                <div className="flex justify-end border-t border-raro-gray-200 pt-5">
                    <Button variant="secondary" size="sm" loading={resolve.isPending} onClick={() => void resolve.mutateAsync(request.id).catch(() => undefined)}>
                        Marcar como resolvido
                    </Button>
                </div>
            )}
        </div>
    );
}

function DayOffReview({ request }: { request: DayOffRequest }) {
    const register = useMarkDayOffRegistered();
    const meta = describeRequestStatus(request);
    const canRegister = request.status === 'approved' && !request.dpRegistration;

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
                <StatusPill label={meta.label} tone={meta.tone} />
                <span className="text-xs text-raro-gray-500">{request.totalDays.toString().replace('.', ',')} dia(s)</span>
            </div>

            <section className="space-y-2">
                <h4 className="text-sm font-semibold text-raro-gray-800">Datas</h4>
                <ul className="space-y-1 text-sm text-raro-gray-700">
                    {formatDayOffDates(request.days).map(line => (
                        <li key={line}>{line}</li>
                    ))}
                </ul>
            </section>

            <ClientApprovalSummary request={request} />

            {request.reason && <Note title="Motivo do colaborador" text={request.reason} />}

            {request.dpRegistration ? (
                <p className="text-sm font-medium text-emerald-700">Já lançado no ponto.</p>
            ) : canRegister ? (
                <div className="flex justify-end border-t border-raro-gray-200 pt-5">
                    <Button loading={register.isPending} onClick={() => void register.mutateAsync(request.id).catch(() => undefined)}>
                        Marcar como lançado no ponto
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

function Note({ title, text }: { title: string; text: string }) {
    return (
        <div className="rounded-xl border border-raro-gray-200 bg-raro-gray-50 px-4 py-3 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-raro-gray-500">{title}</p>
            <p className="mt-1 whitespace-pre-wrap text-raro-gray-800">{text}</p>
        </div>
    );
}

